/**
 * Deck.gl Overlay Hook for Visitor Map
 *
 * Renders props using Deck.gl IconLayer for smooth continuous zoom scaling
 * (eliminates MapLibre's integer-stop popping).
 *
 * Architecture:
 * - 2 IconLayers: prop shadows, props
 * - Props use sizeUnits: 'meters' (scale with map)
 * - Shadows use mask: true for true silhouettes (not dark tints)
 */

import { useRef, useEffect, useState, useMemo } from "react";
import type { MapboxOverlay as MapboxOverlayType } from "@deck.gl/mapbox";
import type { IconLayer as IconLayerType, IconLayerProps } from "@deck.gl/layers";
import type { PickingInfo } from "@deck.gl/core";
import type { Map as MapLibreMap } from "maplibre-gl";
import {
  STICKER_DEFAULT_Z_ORDER,
  getLayerZOrder,
  type ThemeConfig,
  convertSpriteToDeckIconMapping,
  createMaskedMapping,
  DEFAULT_SHADOW_OFFSET,
  DEFAULT_SHADOW_OPACITY,
  opacityToRgba,
  PROP_METER_SIZES,
  DEFAULT_PROP_METER_SIZE,
  getMaxDensityForZoom,
  resolveStickerZoomRange,
  type DeckIconMapping,
} from "@wondermaps/maps";
import { PROP_TYPES } from "@wondermaps/db/client";
import type { Layer } from "../MapCanvas";
import {
  buildStickerIconLayer,
  buildStickerShadowLayer,
  findStickerBeforeId,
  type StickerFeature,
} from "./buildStickerIconLayer";

/**
 * Visitor sticker payload shape (mirrors `IllustrationWithAsset` in
 * `apps/api/src/routes/maps/types.ts`). Threaded through MapCanvas from the
 * `version.illustrations[]` payload field.
 */
export interface VisitorIllustration {
  id: string;
  assetSlug: string;
  center: { type: "Point"; coordinates: [number, number] };
  widthMeters: number;
  rotationDeg: number;
  opacity: number;
  // PR9 — null means inherit theme.stickers.defaultMinZoom at render time.
  minZoom: number | null;
  maxZoom: number | null;
  zOrderOverride: number | null;
  sortOrder: number;
}

// Shadow zoom range base constants (shifted by zoomOffset for larger maps)
const SHADOW_ZOOM_FAR_BASE = 16;
const SHADOW_ZOOM_CLOSE_BASE = 20;
const SHADOW_ZOOM_REFERENCE_BASE = 18;

// Dynamic imports for Deck.gl - loaded on first use for better mobile performance
// Reduces initial bundle by ~200KB, loads in parallel during map init
let MapboxOverlay: typeof MapboxOverlayType | null = null;
let IconLayer: typeof IconLayerType | null = null;

async function loadDeckGL() {
  if (!MapboxOverlay || !IconLayer) {
    const [mapboxModule, layersModule] = await Promise.all([
      import("@deck.gl/mapbox"),
      import("@deck.gl/layers"),
    ]);
    MapboxOverlay = mapboxModule.MapboxOverlay;
    IconLayer = layersModule.IconLayer;
  }
  return { MapboxOverlay, IconLayer };
}

/**
 * Extended IconLayer props with beforeId for MapboxOverlay interleaved mode
 * beforeId is valid at runtime but not in Deck.gl TypeScript types
 */
type IconLayerPropsWithBeforeId<D> = IconLayerProps<D> & { beforeId?: string };

/**
 * Per-item shadow config for Deck.gl
 */
interface ItemShadowConfig {
  enabled: boolean;
  offset: number;
  opacity: number;
  offsetZoom?: { farOffset: number; closeOffset: number };
}

/**
 * Prop feature data for Deck.gl IconLayer
 */
interface PropFeature {
  position: [number, number];
  icon: string;
  size: number; // meters
  densityRank: number;
  propType: string;
  shadow: ItemShadowConfig;
}

interface UseDeckOverlayOptions {
  map: MapLibreMap | null;
  layers: Layer[];
  theme: ThemeConfig;
  isMapReady: boolean;
  zoomOffset?: number;
  /**
   * Visitor sticker placements (plan 120, PR5). When present, rendered on the
   * same MapboxOverlay as the props with `pickable: false` — clicks fall
   * through to the underlying POI/path layers, matching the "decoration only"
   * constraint from plan §25.
   */
  illustrations?: VisitorIllustration[];
  /** Loaded sticker atlas. Same `useStickerAtlas` hook the admin overlay uses. */
  stickerAtlas?: {
    atlasUrl: string;
    mapping: Record<string, DeckIconMapping>;
  } | null;
}

/**
 * Generate a stable pseudo-random density rank from coordinates
 * Uses simple hash to ensure same position always gets same rank
 */
function stableDensityRank(lng: number, lat: number): number {
  // Simple hash: multiply coords by large primes, take fractional part
  const hash = (lng * 12345.6789 + lat * 98765.4321) % 1;
  return Math.abs(hash);
}

/**
 * Extract layer type from MapLibre layer ID
 * Handles suffix patterns like -fill, -line, -symbol, -shadow, -extrusion
 */
function extractLayerTypeFromId(layerId: string): string {
  return layerId
    .replace(/-fill$/, "")
    .replace(/-line$/, "")
    .replace(/-symbol$/, "")
    .replace(/-shadow$/, "")
    .replace(/-extrusion$/, "");
}

/**
 * Find the beforeId for inserting a Deck.gl layer at a specific z-order
 * Returns the first MapLibre layer ID with z-order > targetZOrder
 */
function findBeforeId(
  map: MapLibreMap,
  targetZOrder: number,
  theme: ThemeConfig
): string | undefined {
  const layers = map.getStyle()?.layers || [];
  for (const layer of layers) {
    const layerType = extractLayerTypeFromId(layer.id);
    const layerZOrder = getLayerZOrder(layerType, theme);
    if (layerZOrder > targetZOrder) {
      return layer.id;
    }
  }
  return undefined;
}

/**
 * Extract and sort prop features from layers for Deck.gl rendering
 */
function extractAndSortFeatures(
  layers: Layer[],
  theme: ThemeConfig
): PropFeature[] {
  const propFeatures: PropFeature[] = [];

  for (const layer of layers) {
    if (!layer.geom) continue;

    if (layer.layerType === "prop") {
      const styleJson = layer.styleJson as {
        propType?: string;
        featureProperties?: Array<{ density_rank: number }>;
        densityRank?: number;
      } | null;
      const propType = styleJson?.propType || "tree";
      const propDef = PROP_TYPES.find((p) => p.slug === propType);
      const iconSlug = propDef?.icon || "prop-tree";
      // Base meter size scaled by theme iconSize (default ~0.5 = 1x scale)
      const baseMeterSize = PROP_METER_SIZES[propType] ?? DEFAULT_PROP_METER_SIZE;
      const propConfig = theme.props?.[propType];
      const themeIconSize = propConfig?.iconSize ?? 0.5;
      // Scale: iconSize 0.5 = 1x, iconSize 1.0 = 2x, iconSize 0.25 = 0.5x
      const meterSize = baseMeterSize * (themeIconSize / 0.5);
      // Per-item shadow config
      const shadowConfig: ItemShadowConfig = {
        enabled: propConfig?.shadow?.enabled ?? false,
        offset: propConfig?.shadow?.offset ?? DEFAULT_SHADOW_OFFSET,
        opacity: propConfig?.shadow?.opacity ?? DEFAULT_SHADOW_OPACITY,
        offsetZoom: propConfig?.shadow?.offsetZoom,
      };

      if (layer.geom.type === "GeometryCollection") {
        // Scattered props
        const featureProps = styleJson?.featureProperties;
        layer.geom.geometries.forEach((g, i) => {
          if (g.type === "Point") {
            const coords = g.coordinates as [number, number];
            propFeatures.push({
              position: coords,
              icon: iconSlug,
              size: meterSize,
              // Use stored density_rank or stable hash of coords (avoids jitter on re-render)
              densityRank: featureProps?.[i]?.density_rank ?? stableDensityRank(coords[0], coords[1]),
              propType,
              shadow: shadowConfig,
            });
          }
        });
      } else if (layer.geom.type === "Point") {
        // Single prop (stamp or individual OSM tree)
        const coords = layer.geom.coordinates as [number, number];
        propFeatures.push({
          position: coords,
          icon: iconSlug,
          size: meterSize,
          densityRank: styleJson?.densityRank ?? stableDensityRank(coords[0], coords[1]),
          propType,
          shadow: shadowConfig,
        });
      }
    }
  }

  // Sort props by z-order (lower renders first = bottom)
  propFeatures.sort((a, b) => {
    const aZ = theme.props?.[a.propType]?.zOrder ?? 7.5;
    const bZ = theme.props?.[b.propType]?.zOrder ?? 7.5;
    return aZ - bZ;
  });

  return propFeatures;
}

/**
 * Get shadow direction from theme (default SE)
 */
function getShadowDirection(theme: ThemeConfig): { x: number; y: number } {
  return theme.shadowDirection ?? { x: 1, y: 1 };
}

/**
 * Compute per-item shadow offset based on zoom and direction
 */
function computeItemShadowOffset(
  shadow: ItemShadowConfig,
  direction: { x: number; y: number },
  zoom: number,
  zo: number
): [number, number] {
  if (!shadow.enabled) return [0, 0];

  let offset: number;
  if (shadow.offsetZoom) {
    // Interpolate between far and close offsets
    const { farOffset, closeOffset } = shadow.offsetZoom;
    const zoomRange = SHADOW_ZOOM_CLOSE_BASE - SHADOW_ZOOM_FAR_BASE;
    const t = Math.max(0, Math.min(1, (zoom - (SHADOW_ZOOM_FAR_BASE + zo)) / zoomRange));
    offset = farOffset + (closeOffset - farOffset) * t;
  } else {
    offset = shadow.offset;
  }

  // Apply direction and zoom scaling (scale relative to reference zoom)
  const zoomScale = Math.pow(2, zoom - (SHADOW_ZOOM_REFERENCE_BASE + zo));
  return [offset * direction.x * zoomScale, offset * direction.y * zoomScale];
}

/**
 * Create prop shadow IconLayer (mask: true for silhouettes)
 */
function createPropShadowLayer(
  IconLayerClass: typeof IconLayerType,
  data: PropFeature[],
  shadowMapping: Record<string, DeckIconMapping>,
  atlas: string,
  beforeId: string | undefined,
  direction: { x: number; y: number },
  zoom: number,
  zo: number
) {
  // Filter to only items with shadow enabled
  const shadowData = data.filter((d) => d.shadow.enabled);

  const props: IconLayerPropsWithBeforeId<PropFeature> = {
    id: "deck-prop-shadows",
    data: shadowData,
    iconAtlas: atlas,
    iconMapping: shadowMapping,
    getIcon: (d: PropFeature) => d.icon,
    getPosition: (d: PropFeature) => d.position,
    getSize: (d: PropFeature) => d.size,
    sizeUnits: "meters",
    sizeMinPixels: 8,
    sizeMaxPixels: 200,
    getPixelOffset: (d: PropFeature) => computeItemShadowOffset(d.shadow, direction, zoom, zo),
    getColor: (d: PropFeature) => opacityToRgba(d.shadow.opacity),
    billboard: false,
    beforeId,
  };
  return new IconLayerClass(props);
}

/**
 * Create prop icon IconLayer
 */
function createPropIconLayer(
  IconLayerClass: typeof IconLayerType,
  data: PropFeature[],
  mapping: Record<string, DeckIconMapping>,
  atlas: string,
  beforeId: string | undefined,
  map: MapLibreMap
) {
  const props: IconLayerPropsWithBeforeId<PropFeature> = {
    id: "deck-props",
    data,
    iconAtlas: atlas,
    iconMapping: mapping,
    getIcon: (d: PropFeature) => d.icon,
    getPosition: (d: PropFeature) => d.position,
    getSize: (d: PropFeature) => d.size,
    sizeUnits: "meters",
    sizeMinPixels: 8,
    sizeMaxPixels: 200,
    billboard: false,
    pickable: true,
    onClick: (info: PickingInfo<PropFeature>) => {
      if (info.object) {
        console.log("Prop clicked:", info.object);
      }
    },
    onHover: (info: PickingInfo<PropFeature>) => {
      map.getCanvas().style.cursor = info.object ? "pointer" : "";
    },
    beforeId,
  };
  return new IconLayerClass(props);
}

/**
 * Hook that manages Deck.gl overlay for smooth prop scaling
 *
 * Usage in MapCanvas:
 * ```
 * useDeckOverlay({ map: mapRef.current, layers, theme: currentTheme, isMapReady });
 * ```
 */
export function useDeckOverlay({
  map,
  layers,
  theme,
  isMapReady,
  zoomOffset = 0,
  illustrations,
  stickerAtlas,
}: UseDeckOverlayOptions): void {
  const zo = zoomOffset;
  const overlayRef = useRef<MapboxOverlayType | null>(null);
  const [zoom, setZoom] = useState(18);
  const [deckReady, setDeckReady] = useState(false);

  // Atlas and mapping loaded once
  const [propAtlas, setPropAtlas] = useState<string | null>(null);
  const [propMapping, setPropMapping] = useState<Record<
    string,
    DeckIconMapping
  > | null>(null);

  // Create masked mapping for shadow silhouettes
  const shadowPropMapping = useMemo(() => {
    return propMapping ? createMaskedMapping(propMapping) : null;
  }, [propMapping]);

  // Load atlas on mount
  useEffect(() => {
    async function loadAtlas() {
      const suffix = window.devicePixelRatio > 1 ? "@2x" : "";
      const propJson = await fetch(`/sprites/prop-icons${suffix}.json`).then((r) => r.json());
      setPropAtlas(`/sprites/prop-icons${suffix}.png`);
      setPropMapping(convertSpriteToDeckIconMapping(propJson));
    }
    loadAtlas();
  }, []);

  // Create overlay ONCE (loads Deck.gl dynamically)
  // Uses requestIdleCallback to defer initialization until after MapLibre settles
  useEffect(() => {
    if (!map || !isMapReady) return;
    let cancelled = false;

    (async () => {
      // Wait for idle time before creating second WebGL context
      // This prevents context loss from parallel GPU resource allocation
      await new Promise<void>((resolve) => {
        if ("requestIdleCallback" in window) {
          (window as Window).requestIdleCallback(() => resolve(), { timeout: 500 });
        } else {
          setTimeout(resolve, 100);
        }
      });

      if (cancelled) return;

      const { MapboxOverlay: OverlayClass } = await loadDeckGL();
      if (cancelled) return;

      // a11y note (plan 120, PR5): in `interleaved: true` mode Deck.gl draws
      // straight into the existing MapLibre canvas, so there's no separate
      // DOM container that needs `aria-hidden`. Screen reader visibility is
      // governed by the parent `<div role="application">` in MapCanvas.tsx,
      // which already labels the map as not screen-reader navigable; the
      // visitor falls through to the list view via the recenter/list buttons.
      const overlay = new OverlayClass({
        interleaved: true, // CRITICAL for beforeId layer ordering
        layers: [],
      });
      map.addControl(overlay);
      overlayRef.current = overlay;
      setDeckReady(true);
    })();

    return () => {
      cancelled = true;
      if (overlayRef.current) {
        map.removeControl(overlayRef.current);
        overlayRef.current = null;
      }
      setDeckReady(false);
    };
  }, [map, isMapReady]);

  // Track zoom continuously for shadow offset updates
  useEffect(() => {
    if (!map || !isMapReady) return;
    const handleMove = () => setZoom(map.getZoom());
    map.on("move", handleMove);
    setZoom(map.getZoom());
    return () => {
      map.off("move", handleMove);
    };
  }, [map, isMapReady]);

  // EXPENSIVE: Memoize feature extraction (only re-run when layers/theme change)
  const propFeatures = useMemo(() => {
    return extractAndSortFeatures(layers, theme);
  }, [layers, theme]); // NOT zoom-dependent

  // Track density threshold with state - only update when it actually changes
  // This prevents jittering during continuous zoom when threshold is the same
  const [maxDensity, setMaxDensity] = useState(() => getMaxDensityForZoom(zoom));
  useEffect(() => {
    const newDensity = getMaxDensityForZoom(zoom);
    // Use functional update to avoid maxDensity in deps (prevents potential infinite loop)
    setMaxDensity((prev) => (newDensity !== prev ? newDensity : prev));
  }, [zoom]);

  // CHEAP: Filter props by density (re-run only when threshold actually changes)
  const visiblePropFeatures = useMemo(() => {
    return propFeatures.filter((f) => f.densityRank <= maxDensity);
  }, [propFeatures, maxDensity]);

  // Sticker features built once per illustrations/theme change. Atlas slug
  // resolution is direct — visitor payload already inlines `assetSlug`.
  const stickerFeatures = useMemo<StickerFeature[]>(() => {
    if (!illustrations?.length) return [];
    return illustrations.map((inst) => {
      const { minZoom, maxZoom } = resolveStickerZoomRange(inst, theme);
      return {
        id: inst.id,
        position: [inst.center.coordinates[0], inst.center.coordinates[1]],
        widthMeters: inst.widthMeters,
        iconSlug: inst.assetSlug,
        rotationDeg: inst.rotationDeg,
        opacity: inst.opacity,
        minZoom,
        maxZoom,
      };
    });
  }, [illustrations, theme]);

  // Masked atlas mapping for the silhouette shadow. Memoized off the atlas
  // mapping ref so the IconLayer's iconMapping prop stays stable.
  const stickerShadowMapping = useMemo<Record<string, DeckIconMapping> | null>(
    () => (stickerAtlas ? createMaskedMapping(stickerAtlas.mapping) : null),
    [stickerAtlas],
  );

  // Update layers on every zoom change for smooth shadow scaling
  useEffect(() => {
    if (
      !overlayRef.current ||
      !deckReady ||
      !IconLayer ||
      !map ||
      !propAtlas ||
      !shadowPropMapping ||
      !propMapping
    )
      return;

    // Get global shadow direction
    const shadowDirection = getShadowDirection(theme);

    // Props at z-order 7.75 (middle of 7.5-7.9 range)
    const propBeforeId = findBeforeId(map, 7.75, theme);

    const deckLayers: IconLayerType[] = [
      createPropShadowLayer(
        IconLayer,
        visiblePropFeatures,
        shadowPropMapping,
        propAtlas,
        propBeforeId,
        shadowDirection,
        zoom,
        zo
      ),
      createPropIconLayer(
        IconLayer,
        visiblePropFeatures,
        propMapping,
        propAtlas,
        propBeforeId,
        map
      ),
    ];

    // Sticker render (plan 120, PR5). Visitor variant: pickable:false so
    // clicks on a sticker fall through to the underlying POI/path, matching
    // the "decoration not wayfinding" constraint from plan §25. Same Deck
    // overlay as props — one MapboxOverlay per map keeps the WebGL context
    // budget tight on cellular.
    if (
      stickerFeatures.length > 0 &&
      stickerAtlas &&
      stickerShadowMapping
    ) {
      const stickerBeforeId = findStickerBeforeId(
        map,
        theme,
        STICKER_DEFAULT_Z_ORDER,
      );
      // Plan 120 v2: theme-level sticker shadow toggle. Default ON.
      // When the admin disables shadows we drop the shadow IconLayer
      // entirely — same Deck draw count as the original PR4 path.
      const shadowsEnabled = theme.stickers?.shadowsEnabled ?? true;
      if (shadowsEnabled) {
        deckLayers.push(
          buildStickerShadowLayer({
            IconLayerClass: IconLayer,
            features: stickerFeatures,
            shadowMapping: stickerShadowMapping,
            atlasUrl: stickerAtlas.atlasUrl,
            currentZoom: zoom,
            beforeId: stickerBeforeId,
            shadowDirection,
            shadowOpacity: theme.stickers?.shadowOpacity,
            shadowOffsetMultiplier: theme.stickers?.shadowOffsetMultiplier,
            id: "deck-sticker-shadows",
          }),
        );
      }
      deckLayers.push(
        buildStickerIconLayer({
          IconLayerClass: IconLayer,
          features: stickerFeatures,
          atlas: { atlasUrl: stickerAtlas.atlasUrl, mapping: stickerAtlas.mapping },
          currentZoom: zoom,
          pickable: false,
          beforeId: stickerBeforeId,
          id: "deck-stickers",
        }),
      );
    }

    overlayRef.current.setProps({ layers: deckLayers });
  }, [
    deckReady,
    visiblePropFeatures,
    zoom,
    zo,
    propAtlas,
    shadowPropMapping,
    propMapping,
    map,
    theme,
    stickerFeatures,
    stickerAtlas,
    stickerShadowMapping,
  ]);
}
